// Pure, dataset-agnostic helpers shared by every questionnaire (RT103765
// Rakennustekniikka, RT103766 Talotekniikka/Sähkö/Tietotekniikka, and any
// future one) — see services/kayttoikaDatasets.js for where each dataset's
// own JSON is turned into a tree via buildTree below.
//
// A dataset's own section breadcrumb is always [rootLabel, group,
// leafSection] (e.g. ["RAKENNUSTEKNIIKKA", "Talo", "Julkisivut"]) — the
// first element is constant per dataset and dropped here rather than shown
// as a redundant top level in the UI. A question missing a group/leaf
// (shouldn't happen for hasOptions:true rows, but kept defensive) falls
// into "Muu" so nothing silently disappears from the tree.
const FALLBACK = 'Muu';

export function buildTree(questions) {
  const groups = new Map(); // group -> Map(leafSection -> question[])
  for (const q of questions) {
    const group = q.section?.[1] || FALLBACK;
    const leaf = q.section?.[2] || FALLBACK;
    if (!groups.has(group)) groups.set(group, new Map());
    const leaves = groups.get(group);
    if (!leaves.has(leaf)) leaves.set(leaf, []);
    leaves.get(leaf).push(q);
  }
  return Array.from(groups.entries()).map(([group, leaves]) => ({
    group,
    leafSections: Array.from(leaves.entries()).map(([leaf, qs]) => ({
      leaf,
      questions: qs,
    })),
  }));
}

// A chosen answer ({ code, optionId }) only ever stores a reference — this
// is the single place that resolves it back into the real kayttoika/
// huoltovali/huomautukset data, so every reader (now or anything built
// later) stays in sync with the current reference dataset instead of a
// value snapshotted at answer-time. Takes the relevant dataset's own
// questionsByCode (see kayttoikaDatasets.js) since a code is only unique
// within its own dataset's lookup, not globally guaranteed.
//
// Resolved by optionId, not label: a handful of rakenneosa (e.g. 1131
// Salaojat, 1251 Parvekkeet) genuinely repeat the exact same label twice,
// split only by era/code-compliance (constructionPeriod or a Huomautukset
// note) — label alone can't tell those apart, so it's never the lookup
// key, only a display string. optionId (assigned at generation time, see
// build_questionnaire.py) is stable and unique per option regardless of
// label collisions.
export function resolveAnswer(questionsByCode, code, optionId) {
  const q = questionsByCode[code];
  if (!q) return null;
  const option = q.vaihtoehdot?.find(o => o.optionId === optionId);
  if (!option) return null;
  return { question: q, option };
}

// Once an answer also carries a rasitusluokka (the assessor's own judgment
// of that rakenneosa's exposure/usage conditions — not derivable from the
// chosen option), this picks the one applicable lifespan off the resolved
// option instead of leaving all three RL columns ambiguous.
export function applicableLifespan(option, rasitusluokka) {
  if (!option) return null;
  return option[`lifespanRL${rasitusluokka}`] ?? null;
}

// A step's value is "short" when it's one of the compact shapes the
// timeline's narrow columns were built for (a bare number, a range, or
// one of the R/J/Saavutettu symbols) — anything else is free text (common
// in Talotekniikka's huoltoväli/tarkastusväli, e.g. full maintenance
// instructions), which wrapped into a ~100px-wide column renders as one
// word per line for 15+ lines. Used to route long values to their own
// full-width line below the timeline instead of squeezing them into it —
// see RTQuestionRow's steps rendering.
export function isShortValue(raw) {
  if (!raw) return true;
  const trimmed = raw.trim();
  return (
    /^\d+$/.test(trimmed) ||
    /^\d+\.\.\.\d+$/.test(trimmed) ||
    /^\d+\s*kk$/i.test(trimmed) ||
    /^(R|J|J\/R|Saavutettu)$/.test(trimmed) ||
    /^R\s*\(\d+\)$/.test(trimmed)
  );
}

// A lifespan cell isn't always a number of years — "R"/"J" are the RT
// card's own symbols (R = rakennuksen ikä, J = järjestelmän ikä: that
// rakenneosa's technical lifespan is defined as equal to the building's
// or the system's own lifespan, not a separate fixed figure). Spelled out
// in full here rather than left as a bare letter — a legend line above
// the list isn't something a reader re-checks on every single row, so the
// row itself has to be self-explanatory. "Rakennuksen ikä" also gets the
// building's actual computed age appended (when rakennusvuosi is known) —
// genuinely new information, not just a restatement. A bare number/range
// gets a short "v" unit so it reads as years instead of an unexplained
// digit; already-descriptive text (e.g. "R (30)") is left exactly as
// extracted.
export function formatLifespanCompact(raw, rakennuksenIka) {
  if (!raw) return raw;
  const trimmed = raw.trim();
  if (trimmed === 'R') {
    return rakennuksenIka != null ? `Rakennuksen ikä (≈${rakennuksenIka} v)` : 'Rakennuksen ikä';
  }
  if (trimmed === 'J') return 'Järjestelmän ikä';
  if (trimmed === 'J/R') return 'Järjestelmän/rakennuksen ikä';
  if (/^\d+$/.test(trimmed)) return `${trimmed} v`;
  const range = trimmed.match(/^(\d+)\.\.\.(\d+)$/);
  if (range) return `${range[1]}–${range[2]} v`;
  return trimmed;
}

// Tarkastusväli (inspectionIntervalYears) and huoltoväli (maintenanceInterval)
// are both recurring-period fields, extracted as free text: mostly a bare
// number or "X...Y" range (the "just a number" case this formats, in the
// same short "v" unit as formatLifespanCompact — the timeline step it feeds
// already labels itself "Tarkastus"/"Huolto", so the value only needs to
// carry the number), but some rows already carry a full explanatory
// sentence (e.g. "5 sisapuolinen tarkastus, 2 ulkopuolinen tarkastus") or a
// month-based cadence ("12 kk") — those already read clearly in Finnish, so
// they're left untouched rather than risk mangling a correct sentence.
export function formatInterval(raw) {
  if (!raw) return raw;
  const trimmed = raw.trim();
  if (/^\d+$/.test(trimmed)) return `${trimmed} v`;
  const range = trimmed.match(/^(\d+)\.\.\.(\d+)$/);
  if (range) return `${range[1]}–${range[2]} v`;
  return trimmed;
}
